<template>
    <div role="status" aria-live="polite" class="empty:sr-only">
        <div v-if="busy && landing" class="flex flex-col items-center gap-4">
            <LoadingSpinner class="size-6 text-ink-muted motion-reduce:animate-none" :aria-hidden="!unavailable" />
            <p v-if="!unavailable" class="scaffold-body text-ink-muted">{{ t("teamJoin.joining") }}</p>
        </div>
        <p v-else-if="blocking" class="body p-4">{{ t("teamJoin.joining") }}</p>
    </div>
    <div v-if="failed && landing" class="flex w-full flex-col gap-6">
        <p role="alert" class="body text-ink-muted">{{ t("teamJoin.failed") }}</p>
        <BaseButton theme="primary" :disabled="busy" @click="complete">{{ t("teamJoin.retry") }}</BaseButton>
    </div>
    <div v-else-if="failed" class="flex flex-col gap-3 p-4">
        <BaseNotification theme="error" :text="hasLink ? t('teamJoin.failed') : t('teamJoin.emailFailed')" hide-dismiss />
        <div class="flex gap-3">
            <BaseButton theme="secondary" :disabled="busy" @click="complete">{{ t("teamJoin.retry") }}</BaseButton>
            <BaseButton theme="tertiary" @click="dismiss">{{ t("teamJoin.dismiss") }}</BaseButton>
        </div>
    </div>
    <BaseToast v-if="!landing && teamJoinUnavailableNotice" v-model:open="teamJoinUnavailableNotice" theme="error" :description="t('teamJoin.notFound')" />
    <slot v-if="pageReady || !blocking" />
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import type { UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { acceptEmailInvitations, acceptTeamInvitation } from "~vue/Teams/teamInvitationActions";
import { clearTeamJoin, invitationsChecked, pendingTeamJoin, teamJoinDestination, teamJoinUnavailableNotice } from "~vue/Teams/teamJoinContinuation";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{ user?: UserDetails; landing?: boolean; teamsUrl?: string }>();
const emit = defineEmits<{ onboardingRequired: [] }>();
const { t } = useI18n();
const busy = ref(false);
const pageReady = ref(false);
const failed = ref(false);
const hasLink = ref(false);
const blocking = computed(() => busy.value && (props.landing || hasLink.value));
let attemptedInvitationId: string | undefined;
const sessionKey = computed(() => `${props.user?.uuid ?? ""}:${props.user?.team_invitation_check_id ?? ""}`);
const unavailableUrl = computed(() => props.teamsUrl ?? (props.user?.is_teams_enabled ? "/people/?view=teams" : "/"));
const eligible = computed(() => props.user && !props.user.is_anonymous && (props.landing || (props.user.has_onboarded && props.user.is_teams_enabled)));
let attemptedKey: string | null = null;
const unavailable = computed(() => props.landing && props.user?.is_teams_enabled === false);
type JoinDestination = { url: string; unavailable: boolean };
let pendingDestination: JoinDestination | null = null;

async function navigate(destination: JoinDestination): Promise<void> {
    pendingDestination = destination;
    await new Promise<void>((resolve) => {
        router.visit(destination.url, {
            replace: true,
            onSuccess: () => {
                pendingDestination = null;
                if (destination.unavailable) {
                    teamJoinUnavailableNotice.value = true;
                }
            },
            onFinish: () => {
                failed.value = pendingDestination !== null;
                resolve();
            },
        });
    });
}

async function complete(): Promise<void> {
    if (busy.value || !eligible.value) {
        return;
    }
    busy.value = true;
    failed.value = false;
    const key = sessionKey.value;
    const invitation = pendingTeamJoin();
    attemptedInvitationId = invitation?.request_id;
    hasLink.value = invitation !== null || pendingDestination !== null;
    try {
        if (pendingDestination) {
            await navigate(pendingDestination);
            return;
        }
        if (unavailable.value) {
            clearTeamJoin(invitation?.request_id);
            await navigate({ url: unavailableUrl.value, unavailable: false });
            return;
        }
        if (invitation) {
            const destination = teamJoinDestination();
            const result = await acceptTeamInvitation(invitation, key);
            if (result.status === "onboarding_required") {
                emit("onboardingRequired");
                return;
            }
            if (result.status === "unavailable") {
                await navigate({ url: unavailableUrl.value, unavailable: true });
                return;
            }
            if (result.status === "failed") {
                failed.value = true;
                return;
            }
            if (props.landing || destination === "team") {
                await navigate({ url: result.teamUrl, unavailable: false });
            }
            return;
        }
        if (props.landing || invitationsChecked(key)) {
            return;
        }
        const joined = await acceptEmailInvitations(key);
        if (!joined) {
            failed.value = true;
            return;
        }
        if (joined.team_uuids.length) {
            router.reload();
        }
    } catch {
        failed.value = true;
    } finally {
        busy.value = false;
        pageReady.value = true;
    }
}

function dismiss(): void {
    failed.value = false;
    pendingDestination = null;
    if (hasLink.value) {
        clearTeamJoin(attemptedInvitationId);
        hasLink.value = false;
        void complete();
    }
}

watch(
    [eligible, sessionKey],
    () => {
        if (!eligible.value || attemptedKey === sessionKey.value) {
            return;
        }
        attemptedKey = sessionKey.value;
        void complete();
    },
    { immediate: true },
);
</script>
